// ============================================
// components/friends/FriendsList.js
// ============================================
import React from 'react';
import { Box, Typography, List, ListItem, Paper, Stack, Skeleton } from '@mui/material';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import { isPorchlightActive } from '../../models/porchlightModel';
import { getFriendDisplayName } from '../../utils/helpers';

const FriendsList = ({ friends, isLightOn, loading = false }) => {
  const debugFriends = (() => {
    try { return typeof window !== 'undefined' && window.localStorage.getItem('DEBUG_FRIENDS') === 'true'; } catch (_) { return false; }
  })();
  return (
    <Box sx={{ width: '100%', maxWidth: 400, mt: 3, mb: 3, mx: 'auto' }}>
      <Typography
        variant="h6"
        component="h2"
        sx={{
          color: 'primary.main',
          fontWeight: 700,
          mb: 2,
          fontSize: { xs: '1.1rem', sm: '1.25rem' },
          textAlign: 'center'
        }}
      >
        Friends
      </Typography>

      <List 
        sx={{ maxHeight: 260, overflowY: 'auto', pr: 1, overflowX: 'visible', pl: 0.5 }}
        data-testid="friends-list"
      >
        {loading && Array.from({ length: 4 }).map((_, i) => (
          <ListItem key={`skeleton-${i}`} sx={{ px: 0, py: 1, display: 'flex', alignItems: 'center' }}>
            <Skeleton variant="rounded" width={34} height={34} sx={{ mr: 2, borderRadius: 1 }} />
            <Box sx={{ flexGrow: 1 }}>
              <Skeleton width="60%" />
            </Box>
          </ListItem>
        ))}
        {!loading && friends.map((friend) => {
          const friendActive = isPorchlightActive(friend.porchlightUntil);
          const displayName = getFriendDisplayName(friend);
          if (debugFriends) {
            try {
              // Log minimal fields to diagnose blank names
              // eslint-disable-next-line no-console
              console.debug('[FriendsList] Render friend', {
                id: friend.id,
                friendUid: friend.friendUid,
                friendName: friend.friendName,
                firstName: friend.firstName,
                lastName: friend.lastName,
                email: friend.email,
                porchlightUntil: friend.porchlightUntil,
                computedName: displayName
              });
            } catch (_) {}
          }
          return (
            <ListItem 
              key={friend.id} 
              sx={{ px: 0, py: 1, display: 'flex', alignItems: 'center', overflow: 'visible' }}
              data-testid="friend-item"
            >
              <img
                src={friendActive ? '/on_badge.png' : '/off_badge.png'}
                alt={friendActive ? 'On badge' : 'Off badge'}
                className="badge-animated"
                loading="lazy"
                style={{ 
                  width: 34, 
                  height: 34, 
                  marginRight: 16, 
                  verticalAlign: 'middle', 
                  borderRadius: 6 
                }}
              />
              {/* Marker elements to support Cypress status assertions */}
              <span
                style={{ display: 'none' }}
                data-testid={friendActive ? 'friend-item-active' : 'friend-item-inactive'}
              />
              <Typography
                variant="body1"
                sx={{
                  color: 'primary.main',
                  fontSize: { xs: '1rem', sm: '1.08rem' },
                  fontWeight: 600
                }}
              >
                {displayName}
              </Typography>
            </ListItem>
          );
        })}
        {!loading && friends.length === 0 && (
          <ListItem sx={{ px: 0, py: 1, justifyContent: 'center' }}>
            <Paper elevation={0} variant="glass" sx={{
              width: '100%',
              maxWidth: 400,
              px: 3,
              py: 3,
              borderRadius: 2
            }} data-testid="empty-friends-card">
              <Stack spacing={1.5} alignItems="center" textAlign="center">
                <PersonAddAlt1Icon sx={{ color: isLightOn ? 'primary.contrastText' : 'text.primary' }} />
                <Typography variant="subtitle1" component="p" sx={{ color: isLightOn ? 'primary.contrastText' : 'text.primary', fontWeight: 600 }}>
                  Invite friends
                </Typography>
                <Typography variant="body2" sx={{ color: isLightOn ? 'primary.contrastText' : 'text.primary' }}>
                  Invite friends to see when they’re available.
                </Typography>
              </Stack>
            </Paper>
          </ListItem>
        )}
      </List>
    </Box>
  );
};

export default FriendsList;


